// Catalog.ID Client — Entry Point
// ============================================================================

import { Shell } from '@catalog-shell';
import { buildShellConfig, ORG_ROLE_GATED_PANES, orgRolesCanManage } from './shell-config';
import { registerPaneTypes } from './register-pane-types';
import { session } from './state/session';
import { keys } from './state/keys';
import { identity } from './state/identity';
import * as deviceStorage from './state/device-storage';
import * as accountApi from './api/account';
import { renderLoginView, renderDeviceUnlockView, type LoginViewOptions } from './auth/login-view';
import { loginOptionsAfterSessionLoss } from './auth/session-lost';
import { loadOnboardingStash } from './auth/onboarding-stash';
import { renderRegistrationWizard } from './auth/registration-wizard';
import { renderOrgRegistrationWizard } from './auth/org-registration-wizard';
import { renderDelegateActivationWizard } from './auth/delegate-activation-wizard';
import { renderRegisterEntryView, type InvitationRoute } from './auth/register-entry-view';
import {
    activationRefFromUrl, activationRefFromStash, activationRefMatchesStash, type DelegateInvitationRef,
} from './auth/delegate-activation-ref';
import { renderVerifyLanding } from './auth/verify-landing';
import { renderProfileLookup } from './auth/profile-lookup';
import { renderRecoveryView } from './auth/recovery-view';
import { createIdentityPicker } from './components/identity-picker';
import { renderOidcConsentRoute } from './pane-types/oidc-consent-pane';
import { renderCeremonyConsoleStandalone } from './pane-types/ceremony-console-pane';
import { renderChatRootStandalone } from './pane-types/chat-root-pane';
import { renderKeyRotationView } from './auth/key-rotation-view';
import { renderVoucherPurchaseView } from './auth/voucher-purchase-view';
import { eventSocket } from './state/event-socket';
import { api } from './api/client';
import { isDashboardContext, initApiBase, dashboardHref } from './state/dashboard-context';
import { KeyGrantClient } from './key-grant/client';
import {
    TabSessionChannel, TAB_SESSION_PATH, loginTickets, logoutPushAction, announcementAfterSessionLoss,
    type TabAnnouncement,
} from './state/tab-session';
import { logOutEverywhere } from './auth/logout-action';

// ============================================================================
// App State
// ============================================================================

let shell: Shell | null = null;
let keyGrantClient: KeyGrantClient | null = null;

// ============================================================================
// This tab's session, told to the desktop dashboard (2026-10-08)
// ============================================================================
//
// The tab is the authority on who is signed in on the desktop: the dashboard
// follows what this page announces, and the dashboard's own logout logs this
// page out. See state/tab-session.ts for what is announced when, and why only
// definite states are.

let tabSession: TabSessionChannel | null = null;

/**
 * Set by an interactive login (or a registration/activation that ends signed
 * in) in THIS page, spent by the shell start that follows: that announcement
 * carries a one-time login ticket, the bound on the silent consent the tab
 * gives the desktop's own sign-in (decision 1). Never set by an unlock, a
 * reload or an identity switch.
 */
let freshLogin = false;

function announce(announcement: TabAnnouncement | null): void {
    if (announcement) tabSession?.announce(announcement);
}

/** The dashboard logged out (decision 2): this page logs out too. */
async function onDashboardLogout(): Promise<void> {
    const action = logoutPushAction(session.isActive(), tabSession?.lastAnnouncement() ?? null);
    if (action === 'log-out') {
        loginTickets.clear();
        // Reloads to `/`, whose boot announces `signed-out`.
        await logOutEverywhere();
    } else if (action === 're-announce') {
        announce({ state: 'signed-out' });
    }
}

function startTabSessionChannel(): void {
    if (!isDashboardContext() || tabSession) return;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    tabSession = new TabSessionChannel({
        url: `${protocol}//${window.location.host}${TAB_SESSION_PATH}`,
        onLogoutRequested: () => { void onDashboardLogout(); },
    });
    tabSession.start();
}

// ============================================================================
// Routing
// ============================================================================

function getRoute(): { path: string; params: Record<string, string> } {
    const url = new URL(window.location.href);
    const path = url.pathname;

    // Email verification: /verify#<token> — the token rides the fragment so it
    // never reaches any server or access log.
    if (path === '/verify') {
        return { path: '/verify', params: { token: url.hash.slice(1) } };
    }

    const profileMatch = path.match(/^\/profile\/(.+)$/);
    if (profileMatch) return { path: '/profile', params: { cid: profileMatch[1] } };

    // Delegate activation. Printed cards point at /register — the entry page
    // routes on the code's prefix (§14.2) — and this path stays as an alias that
    // understands the same `#code=` fragment. The pre-M15 `?token=` bearer link
    // it used to carry was removed with M20.
    if (path === '/delegate/activate') {
        return { path: '/delegate/activate', params: {} };
    }

    // Registration entry page: /register#code=INV-… — the code rides the
    // FRAGMENT, so it reaches no access log and no Referer, exactly as on
    // /verify. The query is also read, so a hand-retyped link still works.
    if (path === '/register') {
        const fragment = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash);
        const code = fragment.get('code')?.trim() || url.searchParams.get('code')?.trim() || '';
        // A delegate card's link names its organization beside the code
        // (docs/cid-governance.md M7a); the entry page validates it.
        const org = fragment.get('org')?.trim() || url.searchParams.get('org')?.trim() || '';
        return { path: '/register', params: { code, org } };
    }

    // Stripe success URL: /auth/registration-complete?registrationId=reg_xxx
    if (path === '/auth/registration-complete') {
        const registrationId = url.searchParams.get('registrationId') ?? '';
        return { path: '/auth/registration-complete', params: { registrationId } };
    }

    // OIDC consent: /oidc/consent?request_id=...[&login_hint=...]
    if (path === '/oidc/consent') {
        const requestId = url.searchParams.get('request_id') ?? '';
        const loginHint = url.searchParams.get('login_hint') ?? '';
        return { path: '/oidc/consent', params: { requestId, loginHint } };
    }

    return { path, params: {} };
}

// ============================================================================
// View Rendering
// ============================================================================

function showLogin(container: HTMLElement, options?: LoginViewOptions): void {
    renderLoginView(container, {
        onSuccess: () => {
            freshLogin = true;
            // Locked+approved accounts go to key rotation instead of shell
            if (session.getInfo()?.status === 'locked') {
                showKeyRotation(container);
                return;
            }
            // A visitor who came with an organization invitation or voucher
            // signed in to use it: carry on where the code was heading.
            const pending = pendingOrgRegistration;
            if (pending) {
                pendingOrgRegistration = null;
                history.replaceState(null, '', dashboardHref('/register/organization'));
                showOrgRegistrationRoute(container, pending);
                return;
            }
            initShell(container);
        },
        onRegister: () => showRegisterEntry(container),
        onResumeOnboarding: () => showRegistrationResume(container),
    }, options);
}

/**
 * Standalone voucher purchase (registration-restructure §7.3 option (a)). No
 * session required — the purchaser need not have, or ever get, an account.
 */
function showVoucherPurchase(container: HTMLElement): void {
    renderVoucherPurchaseView(container, {
        onExit: () => {
            history.replaceState(null, '', '/');
            showLogin(container);
        },
        onRegister: () => {
            history.replaceState(null, '', dashboardHref('/register'));
            showRegisterEntry(container);
        },
    });
}

function showKeyRotation(container: HTMLElement): void {
    renderKeyRotationView(container, {
        onComplete: () => {
            // Clear device storage — the old device holds pre-rotation keys
            // and the device record may no longer be valid after rotation.
            const cid = session.getCid() ?? deviceStorage.getActiveCid();
            if (cid) deviceStorage.clearDeviceStorage(cid);
            keys.clear();
            session.clear();
            identity.clear();
            window.location.href = '/';
        },
    });
}

/**
 * The anonymous entry page (registration-restructure §14.2): a code field, a
 * scan control, the storefront link, and the two flows by name. `/register` is
 * this page now; the personal wizard has its own route, `/register/personal`,
 * mirroring `/register/organization`.
 */
function showRegisterEntry(container: HTMLElement, options: { initialCode?: string; initialOrgCid?: string } = {}): void {
    // The visitor chose another path; a code kept for after a login is dropped.
    pendingOrgRegistration = null;
    renderRegisterEntryView(container, {
        // Three kinds of invitation since §14.3, and the entry page has just
        // asked the node which this one is. A delegate seat goes to the
        // activation wizard; the other two go to the registration wizard they
        // paid for, carrying the code (the wizard derives the lookup id and the
        // fixed-value salt from it, as the activation wizard already does).
        onInvitation: (code, route: InvitationRoute) => {
            if (route.registrationType === 'personal') {
                history.replaceState(null, '', dashboardHref('/register/personal'));
                showRegistration(container, { invitationCode: code });
                return;
            }
            if (route.registrationType === 'organization') {
                history.replaceState(null, '', dashboardHref('/register/organization'));
                showOrgRegistrationRoute(container, {
                    invitationCode: code,
                    invitationDomain: route.domain ?? undefined,
                });
                return;
            }
            // The organization rides along (M7a): the activation wizard sends
            // begin and activate to its origin.
            const ref = route.orgCid ? { code, orgCid: route.orgCid } : { code };
            history.replaceState(null, '', dashboardHref('/register'));
            showDelegateActivation(container, ref, {
                resume: activationRefMatchesStash(ref, loadOnboardingStash()),
            });
        },
        onPersonal: (voucherCode) => {
            history.replaceState(null, '', dashboardHref('/register/personal'));
            showRegistration(container, { voucherCode });
        },
        onOrganization: (voucherCode) => {
            history.replaceState(null, '', dashboardHref('/register/organization'));
            showOrgRegistrationRoute(container, { voucherCode });
        },
        onCancel: () => {
            history.replaceState(null, '', dashboardHref('/'));
            showLogin(container);
        },
    }, options);
}

function showRegistration(
    container: HTMLElement,
    options: { resume?: boolean; voucherCode?: string; invitationCode?: string } = {},
): void {
    renderRegistrationWizard(container, {
        onComplete: () => {
            freshLogin = true;
            history.replaceState(null, '', '/');
            initShell(container);
        },
        onCancel: () => {
            history.replaceState(null, '', '/');
            showLogin(container);
        },
    }, options);
}

/** An organization registration a visitor asked for before signing in — the
 *  entry page's invitation or voucher, kept across the login it needs. */
let pendingOrgRegistration: { voucherCode?: string; invitationCode?: string; invitationDomain?: string } | null = null;

/**
 * The `/register/organization` route. An organization is founded BY a personal
 * identity, so this needs an active personal session with keys loaded; the entry
 * page reaches it the same way, carrying any registration code the visitor
 * typed, and a session that has to be unlocked first keeps that code across the
 * unlock rather than dropping it.
 */
function showOrgRegistrationRoute(
    container: HTMLElement,
    options: { voucherCode?: string; invitationCode?: string; invitationDomain?: string } = {},
): void {
    if (session.isActive() && keys.isLoaded()) {
        showOrgRegistration(container, options);
        return;
    }
    if (session.isActive()) {
        renderDeviceUnlockView(container, {
            onSuccess: async () => {
                await restoreSessionAndIdentity();
                showOrgRegistration(container, options);
            },
            onRegister: () => showRegisterEntry(container),
        });
        return;
    }
    // Not signed in. An organization is founded BY a personal identity, so sign
    // in first — keeping the code the visitor typed or scanned, or the card's
    // link lands on a login page that then forgets what it was for.
    pendingOrgRegistration = options.invitationCode || options.voucherCode ? options : null;
    showLogin(container, {
        notice: options.invitationCode
            ? 'Sign in with your personal identity to use your organization invitation. No personal '
                + 'identity yet? Register one first (Register, below), then open the invitation link again.'
            : 'Sign in with your personal identity to register an organization.',
    });
}

function showOrgRegistration(
    container: HTMLElement,
    options: {
        resumeFromPayment?: boolean; voucherCode?: string;
        invitationCode?: string; invitationDomain?: string;
    } = {},
): void {
    renderOrgRegistrationWizard(container, {
        onComplete: () => {
            freshLogin = true;
            history.replaceState(null, '', '/');
            initShell(container);
        },
        onCancel: () => {
            history.replaceState(null, '', '/');
            showLogin(container);
        },
    }, options);
}

/**
 * Resume after Stripe redirect. The wizard reads `cid_registration_state_v1` from
 * sessionStorage to recover the wizard state captured at payment time. We pick which
 * wizard to render from `registrationType` in that record. If state is missing
 * (different tab, sessionStorage cleared, or expired), fall back to login with a
 * note — the supersecret is gone and there's nothing to resume.
 */
function showRegistrationResume(container: HTMLElement): void {
    const stash = loadOnboardingStash();
    if (!stash) {
        history.replaceState(null, '', '/');
        showLogin(container);
        return;
    }

    if (stash.registrationType === 'organization') {
        showOrgRegistration(container, { resumeFromPayment: true });
    } else if (stash.registrationType === 'delegate-activation') {
        // The invitation reference (signed id + secret, or a legacy token) rides
        // the stash, so a resume reaches the same invitation without the
        // original URL (§13.3, M13; §13.5, M15).
        showDelegateActivation(container, activationRefFromStash(stash) ?? {}, { resume: true });
    } else {
        showRegistration(container, { resume: true });
    }
}

/**
 * Landing page for the dashboard payment popup. Stripe redirects the popup to
 * our success (`/auth/registration-complete`) or cancel (`/register`) URL, both
 * marked with `popupReturn=1`. The parent pane already confirms payment by
 * polling, so here we just tell the user they can close the window and attempt
 * to self-close it (works because the window was opened via `window.open()`).
 */
/**
 * What the desktop tab shows while the helper has no operator directory to
 * name a Catalog ID node from (operator-codes §7.5): first run offline, or no
 * node certified yet. Re-rendered on every retry with the helper's reason.
 */
function renderDirectoryWait(container: HTMLElement, reason: string): void {
    container.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'cid-boot-wait';
    const heading = document.createElement('h2');
    heading.textContent = 'Connecting to Catalog ID…';
    const note = document.createElement('p');
    note.textContent = reason === 'no-cid-node'
        ? 'No Catalog ID server is listed in the operator directory yet. This page keeps trying.'
        : 'Waiting for the operator directory — check the network connection. This page keeps trying.';
    wrap.appendChild(heading);
    wrap.appendChild(note);
    container.appendChild(wrap);
}

function renderPopupReturn(container: HTMLElement): void {
    const cancelled = !window.location.pathname.includes('registration-complete');
    container.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'cid-popup-return';
    const heading = document.createElement('h2');
    heading.textContent = cancelled ? 'Payment cancelled' : 'Payment received';
    const note = document.createElement('p');
    note.textContent = 'You can close this window and return to the Catalog.ID app.';
    wrap.appendChild(heading);
    wrap.appendChild(note);
    container.appendChild(wrap);
    // Paint first, then self-close; if the host blocks close(), the hint remains.
    setTimeout(() => { try { window.close(); } catch { /* user can close manually */ } }, 300);
}

function showDelegateActivation(
    container: HTMLElement, invitation: DelegateInvitationRef, options: { resume?: boolean } = {},
): void {
    renderDelegateActivationWizard(container, invitation, {
        onComplete: () => {
            freshLogin = true;
            history.replaceState(null, '', '/');
            initShell(container);
        },
        onCancel: () => {
            history.replaceState(null, '', '/');
            showLogin(container);
        },
    }, options);
}

/**
 * Re-read account state after a device unlock. Session info and identity live
 * in memory only, so a reload drops them while the token in localStorage
 * survives; routes that gate on account type or delegate roles need them back.
 */
async function restoreSessionAndIdentity(): Promise<void> {
    try {
        const info = await accountApi.getAccountInfo();
        session.updateInfo({
            cid: info.cid,
            accountType: info.accountType,
            status: info.status,
            isOperator: info.isOperator,
            sessionId: info.sessionId,
        });
        identity.setActive({
            cid: info.cid,
            accountType: info.accountType,
            organizationCid: info.organizationCid ?? undefined,
            roles: info.roles ?? undefined,
        });
    } catch { /* non-fatal — the view handles a missing identity itself */ }
}

function initShell(container: HTMLElement): void {
    if (shell) {
        return;
    }

    container.innerHTML = '';

    const shellContainer = document.createElement('div');
    shellContainer.id = 'cid-shell';
    shellContainer.className = 'cid-shell-container';
    container.appendChild(shellContainer);

    // The organization workspace's roster follows the delegate's roles.
    shell = new Shell('cid-shell', buildShellConfig({ orgRoles: identity.getRoles() }), 'catalog-id-shell-v1');
    registerPaneTypes(shell);
    shell.start();

    // Hide workspaces that don't apply to the current identity type
    const active = identity.getActive();
    const isDelegate = active?.accountType === 'delegate';
    shell.setWorkspaceHidden('organization', !isDelegate);
    shell.setWorkspaceHidden('trust', !identity.isPersonal());
    shell.setWorkspaceHidden('operator', !session.isOperator());

    // A delegate without owner/admin gets an organization roster without the
    // role-gated panes (buildShellConfig); a layout persisted by an owner
    // identity on this device may still carry instances of them, and stored
    // instances render even off-roster — close those too.
    if (isDelegate && !orgRolesCanManage(identity.getRoles())) {
        const orgSurface = shell.getWorkspaceRootSurface('organization');
        if (orgSurface) {
            for (const pane of orgSurface.getPanes()) {
                if (ORG_ROLE_GATED_PANES.includes(pane.typeId)) orgSurface.closePane(pane.instanceId);
            }
        }
    }

    // Wire up real-time event push
    eventSocket.setShell(shell);
    const token = api.getToken();
    if (token && !eventSocket.isConnected()) {
        eventSocket.connect(token);
    }

    // Add identity picker to the action bar
    const picker = createIdentityPicker(shell);
    shell.addActionBarElement(picker);

    // Start the Key Grant client only in dashboard-module context.
    // The standalone catalog.id site has no local backend to connect to; the
    // client's start() is a no-op there but we skip instantiation anyway for
    // clarity.
    if (isDashboardContext() && !keyGrantClient) {
        keyGrantClient = new KeyGrantClient(shell);
        keyGrantClient.start();
    }

    // Signed in AND unlocked: the one state this page announces as an
    // account. The dashboard follows it — signing in as this account, or
    // switching to it. Right after a login in this page the announcement
    // carries the one-time ticket for the silent consent.
    const announcedCid = identity.getActive()?.cid ?? session.getCid();
    if (announcedCid) {
        const loginTicket = freshLogin && isDashboardContext() ? loginTickets.mint(announcedCid) : undefined;
        announce({ state: 'signed-in', cid: announcedCid, ...(loginTicket ? { loginTicket } : {}) });
    }
    freshLogin = false;
}

// ============================================================================
// Bootstrap
// ============================================================================

async function init(): Promise<void> {
    const appEl = document.getElementById('app');
    if (!appEl) throw new Error('Missing #app element');

    // In dashboard mode, fetch the helper service's runtime-config — the entry
    // node and the Catalog ID nodes of the desktop's verified operator
    // directory — so all subsequent API calls go to a node (cross-origin)
    // rather than the helper's loopback origin (which would 404 the API).
    // Until the desktop has a directory the helper answers 503 and this waits,
    // saying so on the page. No-op in standalone mode (returns immediately).
    let waitingForDirectory = false;
    await initApiBase(reason => {
        waitingForDirectory = true;
        renderDirectoryWait(appEl, reason);
    });
    if (waitingForDirectory) appEl.innerHTML = '';

    // A dashboard payment popup (opened by the registration wizard's Payment
    // step) lands here after Stripe redirects to the success/cancel URL, which
    // we mark with `popupReturn=1`. The popup is its own browsing context with
    // empty sessionStorage, so the normal resume/login routing doesn't apply —
    // the parent pane already confirms payment by polling. Show a close hint and
    // self-close instead.
    if (new URLSearchParams(window.location.search).get('popupReturn') === '1') {
        renderPopupReturn(appEl);
        return;
    }

    // Every page in the desktop keeps the session channel open, whatever its
    // route: a dashboard logout must reach it on any screen.
    startTabSessionChannel();

    const route = getRoute();

    // Pre-auth routes that don't require a session
    switch (route.path) {
        case '/verify':
            renderVerifyLanding(appEl, route.params.token);
            return;

        case '/profile':
            renderProfileLookup(appEl, route.params.cid);
            return;

        case '/register':
            // The anonymous entry page (§14.2). An `INV-` code in the fragment
            // proceeds on its own; an `RGV-` one fills the field and asks which
            // kind of account it is paying for.
            showRegisterEntry(appEl, {
                initialCode: route.params.code || undefined,
                initialOrgCid: route.params.org || undefined,
            });
            return;

        case '/register/personal':
            // The personal wizard's own route, so the entry page, the login
            // link and the e2e specs can all reach it directly now that
            // `/register` is the entry page.
            showRegistration(appEl);
            return;

        case '/vouchers':
            // Anonymous on purpose: buying a voucher for somebody else must not
            // require an account of your own.
            showVoucherPurchase(appEl);
            return;

        case '/recovery':
            // Anonymous on purpose: this is the address printed on every
            // Recovery Sheet (Addendum §6.1/§6.4), and the person holding the
            // sheet is a custodian who may have no Catalog.ID of their own.
            renderRecoveryView(appEl);
            return;

        case '/oidc/consent':
            // Consent at this node, or a forward to the account's origin when
            // the identity signing in lives elsewhere (docs/cid-governance.md M7b).
            if (route.params.requestId) {
                renderOidcConsentRoute(appEl, {
                    requestId: route.params.requestId,
                    loginHint: route.params.loginHint,
                }, {
                    onRegister: () => showRegisterEntry(appEl),
                });
            }
            return;

        case '/register/organization':
            showOrgRegistrationRoute(appEl);
            return;

        case '/delegate/activate': {
            // A coded invitation (`#code=INV-…`, §14.2). Re-opening the link after
            // a crash is the natural move, so an in-flight stash for THIS
            // invitation resumes rather than starting over (§13.3).
            const ref = activationRefFromUrl(window.location.href);
            if (ref) {
                showDelegateActivation(appEl, ref, {
                    resume: activationRefMatchesStash(ref, loadOnboardingStash()),
                });
            } else {
                showLogin(appEl);
            }
            return;
        }

        // Ceremony console — where an organization's owner (or a person) signs
        // what the Developer's cl-root needs: countersigned certificate
        // requests, the binding consent, migration consents
        // (docs/cid-governance.md M6). Documented in the operator runbook;
        // nothing in the UI links here. Needs an unlocked session; the console
        // reads the session's roles from the node itself and explains any
        // session that cannot sign.
        case '/ceremony':
            if (session.isActive() && keys.isLoaded()) {
                renderCeremonyConsoleStandalone(appEl);
            } else if (session.isActive()) {
                renderDeviceUnlockView(appEl, {
                    onSuccess: async () => {
                        await restoreSessionAndIdentity();
                        renderCeremonyConsoleStandalone(appEl);
                    },
                    onRegister: () => showRegisterEntry(appEl),
                });
            } else {
                // Keep the destination across login — the operator arrived here
                // from written ceremony instructions, not from the app. A login
                // records only the CID and the account type, so the identity's
                // roles and organization are restored before the console opens
                // (an owner delegate was refused as role-less before M6).
                renderLoginView(appEl, {
                    onSuccess: async () => {
                        await restoreSessionAndIdentity();
                        renderCeremonyConsoleStandalone(appEl);
                    },
                    onRegister: () => showRegisterEntry(appEl),
                });
            }
            return;

        // Chat account root ceremony — the deep link a chat first-run banner
        // sends the user to when it finds no published `chat_root` for the
        // account (chat migration §14 M3; trigger contract in
        // apps/chat/dev/.m3-gui-notes.md). The `/ceremony` shape exactly: the
        // ceremony renders standalone, and an unlock or a login comes first
        // when the session cannot run it. Unlike `/ceremony` this destination
        // IS also reachable from the UI — Settings → Chat Identity — because it
        // is ordinary onboarding rather than a summoned operator rite.
        case '/chat-root':
            if (session.isActive() && keys.isLoaded()) {
                renderChatRootStandalone(appEl);
            } else if (session.isActive()) {
                renderDeviceUnlockView(appEl, {
                    onSuccess: async () => {
                        await restoreSessionAndIdentity();
                        renderChatRootStandalone(appEl);
                    },
                    onRegister: () => showRegisterEntry(appEl),
                });
            } else {
                renderLoginView(appEl, {
                    onSuccess: () => renderChatRootStandalone(appEl),
                    onRegister: () => showRegisterEntry(appEl),
                });
            }
            return;

        case '/auth/registration-complete':
            showRegistrationResume(appEl);
            return;
    }

    // Post-auth: check for existing session
    if (session.isActive()) {
        // The dashboard logged out while no Catalog.ID page was listening: log
        // out now, BEFORE unlocking — an unlocked page would first answer
        // module requests (and announce itself) as the account the member
        // just signed out of.
        if (tabSession && await tabSession.logoutPendingAtBoot()) {
            loginTickets.clear();
            await logOutEverywhere();
            return;
        }
        // Locked+approved accounts go to key rotation
        if (session.getInfo()?.status === 'locked') {
            showKeyRotation(appEl);
            return;
        }
        if (keys.isLoaded()) {
            initShell(appEl);
        } else {
            // Session token persists in localStorage (survives page reload AND a full
            // dashboard/webview restart) but decrypted keys are in-memory only. Show
            // device unlock (not full login) — for "open" mode devices this
            // auto-completes without user interaction. A token that the server
            // no longer honours sends the user to the full login form instead.
            renderDeviceUnlockView(appEl, {
                onSessionLost: (info) => {
                    // A refused token is a signed-out tab; "log in as somebody
                    // else" is not — the next login says who.
                    announce(announcementAfterSessionLoss(info));
                    showLogin(appEl, loginOptionsAfterSessionLoss(info));
                },
                onSuccess: async () => {
                    // Restore session info and identity state (lost on page reload)
                    try {
                        const info = await accountApi.getAccountInfo();
                        session.updateInfo({
                            cid: info.cid,
                            accountType: info.accountType,
                            status: info.status,
                            isOperator: info.isOperator,
                            sessionId: info.sessionId,
                        });
                        identity.setActive({
                            cid: info.cid,
                            accountType: info.accountType,
                            organizationCid: info.organizationCid ?? undefined,
                            roles: info.roles ?? undefined,
                        });
                    } catch { /* non-fatal — shell will work, just no CID in picker */ }

                    if (session.getInfo()?.status === 'locked') {
                        showKeyRotation(appEl);
                    } else {
                        initShell(appEl);
                    }
                },
                onRegister: () => showRegisterEntry(appEl),
            });
        }
        return;
    }

    // Default: show login. No session at all is a definite answer — this tab
    // is signed out, and the dashboard follows. (Every logout path reloads to
    // `/` and lands here, so none of them has to remember to say so.)
    announce({ state: 'signed-out' });
    showLogin(appEl);
}

document.addEventListener('DOMContentLoaded', () => {
    void init().catch(err => {
        console.error('[app] init failed:', err);
    });
});
